import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, ClipboardList, AlertTriangle, CheckCircle2, Building2, Eye, Trash2, FileSearch, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import BuildingFilter from '../components/BuildingFilter';
import { generateInspectionPDF } from '../lib/inspectionPdf';
import { CompanyContext } from '../contexts/CompanyContext';

const INSPECTION_TYPES = [
  { v: 'pre_handover', l: 'Pre-Handover' },
  { v: 'post_handover', l: 'Post-Handover' },
  { v: 'tenant_check_in', l: 'Tenant Check-In' },
  { v: 'tenant_check_out', l: 'Tenant Check-Out' },
  { v: 'dlp', l: 'Defect Liability Period' },
  { v: 'fitout_exit', l: 'Fit-Out Exit' },
  { v: 'periodic', l: 'Periodic' },
  { v: 'snagging', l: 'Snagging' },
  { v: 'other', l: 'Other' },
];

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-amber-100 text-amber-800',
  submitted: 'bg-sky-100 text-sky-800',
  re_inspection: 'bg-violet-100 text-violet-800',
  closed: 'bg-emerald-100 text-emerald-800',
};

function KpiCard({ label, value, sub, color = 'amber', Icon }) {
  const colors = {
    amber: 'from-amber-50 to-yellow-50 border-amber-200 text-amber-900 dark:from-amber-900/20 dark:to-yellow-900/10 dark:text-amber-200',
    rose: 'from-rose-50 to-red-50 border-rose-200 text-rose-900 dark:from-rose-900/20 dark:to-red-900/10 dark:text-rose-200',
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900 dark:from-emerald-900/20 dark:to-teal-900/10 dark:text-emerald-200',
    sky: 'from-sky-50 to-cyan-50 border-sky-200 text-sky-900 dark:from-sky-900/20 dark:to-cyan-900/10 dark:text-sky-200',
  };
  return (
    <Card className={`p-4 bg-gradient-to-br ${colors[color]} border`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1">{value}</div>
          {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
        </div>
        {Icon && <Icon className="w-6 h-6 opacity-40" />}
      </div>
    </Card>
  );
}

export default function Inspections() {
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Filters
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const canWrite = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'TECHNICIAN'].includes(user?.role);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (buildingFilter !== 'all') qp.append('building_id', buildingFilter);
      if (typeFilter !== 'all') qp.append('inspection_type', typeFilter);
      if (statusFilter !== 'all') qp.append('status', statusFilter);
      const suffix = qp.toString() ? `?${qp.toString()}` : '';
      const [list, dash] = await Promise.all([
        axios.get(`${API}/inspections${suffix}`, { withCredentials: true }),
        axios.get(`${API}/inspections/dashboard`, { withCredentials: true }),
      ]);
      setInspections(list.data || []);
      setDashboard(dash.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inspections');
    }
    setLoading(false);
  };

  useEffect(() => {
    axios.get(`${API}/buildings`, { withCredentials: true })
      .then(r => setBuildings(r.data || []))
      .catch(() => setBuildings([]));
  }, []);

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [buildingFilter, typeFilter, statusFilter]);

  const openCreate = () => {
    setForm({
      title: '',
      inspection_type: 'snagging',
      building_id: '',
      unit_number: '',
      inspection_date: new Date().toISOString().slice(0, 10),
      tenant_present: '',
      contractor_present: '',
      summary: '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.building_id) delete payload.building_id;
      const r = await axios.post(`${API}/inspections`, payload, { withCredentials: true });
      toast.success(`Inspection ${r.data.inspection_number} created`);
      setOpen(false);
      fetchAll();
      // jump straight into the detail page
      navigate(`/inspections/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this inspection and all its snags?')) return;
    try {
      await axios.delete(`${API}/inspections/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // Feb-18 — quick PDF export from list row (download or view in-tab)
  const downloadInspectionPdf = async (inspId, viewOnly = false) => {
    const t = toast.loading(viewOnly ? 'Preparing PDF preview…' : 'Building PDF…');
    try {
      const [inspResp, chkResp] = await Promise.all([
        axios.get(`${API}/inspections/${inspId}`, { withCredentials: true }),
        axios.get(`${API}/inspections/${inspId}/checklist-items`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const token = localStorage.getItem('session_token') || '';
      await generateInspectionPDF({
        inspection: inspResp.data,
        snags: inspResp.data.snags || [],
        checklistItems: chkResp.data || [],
        company,
        attachmentsAuth: token,
        view: viewOnly,
      });
      toast.dismiss(t);
      toast.success(viewOnly ? 'Opened PDF preview' : 'Report downloaded');
    } catch (e) {
      toast.dismiss(t);
      toast.error('PDF generation failed');
    }
  };

  return (
    <Layout>
      <div data-testid="inspections-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground dark:text-slate-100 flex items-center gap-2">
              <FileSearch className="w-7 h-7 text-amber-600" /> Inspections &amp; Snagging
            </h1>
            <p className="text-sm text-muted-foreground dark:text-slate-400">
              Pre-handover, post-handover, DLP and periodic inspections — capture defects, photos, sign-off and generate branded reports.
            </p>
          </div>
          {canWrite && (
            <Button data-testid="new-inspection-btn" onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Plus className="w-4 h-4 mr-1" /> New Inspection
            </Button>
          )}
        </div>

        {dashboard && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Total Inspections" value={dashboard.total_inspections} color="sky" Icon={ClipboardList} />
            <KpiCard label="Open Snags" value={dashboard.open_snags} sub={`of ${dashboard.total_snags} total`} color="amber" Icon={AlertTriangle} />
            <KpiCard label="Critical (Open)" value={dashboard.critical_open_snags} color="rose" Icon={AlertTriangle} />
            <KpiCard label="Closure Rate" value={`${dashboard.closure_rate}%`} sub={`${dashboard.closed_snags} closed`} color="emerald" Icon={CheckCircle2} />
          </div>
        )}

        <Card className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <BuildingFilter value={buildingFilter} onChange={setBuildingFilter} buildings={buildings} />
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="filter-type" className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {INSPECTION_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status" className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="re_inspection">Re-inspection</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading inspections…</div>
          ) : inspections.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="inspections-empty">
              No inspections yet. Click <strong>New Inspection</strong> to create your first one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr className="text-left text-xs text-muted-foreground uppercase">
                    <th className="px-4 py-2">Number</th>
                    <th className="px-4 py-2">Title</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Building / Unit</th>
                    <th className="px-4 py-2">Inspector</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2 text-right">Snags (open/total)</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {inspections.map((i, idx) => (
                    <tr key={i.id} data-testid={`inspection-row-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-2 font-mono text-xs">{i.inspection_number}</td>
                      <td className="px-4 py-2 font-medium">{i.title}</td>
                      <td className="px-4 py-2 text-xs capitalize">{(i.inspection_type || '').replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2 text-xs">
                        <div className="flex items-center gap-1"><Building2 className="w-3 h-3 opacity-50" />{i.building_name || '—'}</div>
                        {i.unit_number && <div className="text-muted-foreground">Unit {i.unit_number}</div>}
                      </td>
                      <td className="px-4 py-2 text-xs">{i.inspector_name || '—'}</td>
                      <td className="px-4 py-2 text-xs">{i.inspection_date}</td>
                      <td className="px-4 py-2 text-right">
                        <span className="font-semibold">{i.open_snags ?? 0}</span>
                        <span className="text-slate-400"> / {i.total_snags ?? 0}</span>
                        {(i.critical_snags ?? 0) > 0 && (
                          <span className="ml-2 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                            {i.critical_snags} critical
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLES[i.status] || 'bg-slate-100 text-slate-700'}`}>
                          {(i.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          <Button data-testid={`inspection-open-${idx}`} size="sm" variant="outline" onClick={() => navigate(`/inspections/${i.id}`)} title="Open / Edit">
                            <Eye className="w-3.5 h-3.5 mr-1" /> Open
                          </Button>
                          <Button data-testid={`inspection-view-pdf-${idx}`} size="sm" variant="outline" onClick={() => downloadInspectionPdf(i.id, true)} title="View PDF in new tab">
                            <FileSearch className="w-3.5 h-3.5" />
                          </Button>
                          <Button data-testid={`inspection-download-pdf-${idx}`} size="sm" variant="outline" onClick={() => downloadInspectionPdf(i.id, false)} title="Download PDF">
                            <FileDown className="w-3.5 h-3.5" />
                          </Button>
                          {canWrite && (
                            <Button data-testid={`inspection-del-${idx}`} size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(i.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Create dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="new-inspection-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Inspection</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title *</Label>
                <Input data-testid="insp-title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Tower B — Apt 1204 handover snag" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={form.inspection_type || 'snagging'} onValueChange={(v) => setForm({ ...form, inspection_type: v })}>
                    <SelectTrigger data-testid="insp-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INSPECTION_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Inspection Date</Label>
                  <Input type="date" value={form.inspection_date || ''} onChange={(e) => setForm({ ...form, inspection_date: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Building</Label>
                  <Select value={form.building_id || 'none'} onValueChange={(v) => setForm({ ...form, building_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="insp-building"><SelectValue placeholder="Select building" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Unit / Apartment</Label>
                  <Input value={form.unit_number || ''} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} placeholder="e.g. 1204" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tenant Present</Label>
                  <Input value={form.tenant_present || ''} onChange={(e) => setForm({ ...form, tenant_present: e.target.value })} />
                </div>
                <div><Label>Contractor Present</Label>
                  <Input value={form.contractor_present || ''} onChange={(e) => setForm({ ...form, contractor_present: e.target.value })} />
                </div>
              </div>
              <div><Label>Summary / Notes</Label>
                <Textarea rows={3} value={form.summary || ''} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="insp-save-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? 'Creating…' : 'Create & Open'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
